import { DashboardCard } from "./dashboard-card";
import { useAccountContext } from "../account-context";
import { formatCurrency } from "@/lib/utils";
import { Spinner } from "@/components/spinner";
import { MarginHealthBar, type PositionForHealth } from "@/features/margin-panel/margin-health-bar";
import { useMemo } from "react";
import { useTradingSettings } from "@/features/trading-settings/trading-settings-context";

export const AccountHealthCard = () => {
  const { accountSummary, positions, isLoading } = useAccountContext();
  const { accountMode } = useTradingSettings();

  // Calculate snapshot values from positions (not live-updating)
  // These values are calculated once when component mounts/data loads
  const snapshotUsedMargin = useMemo(() => {
    if (positions.length === 0) return 0;
    return positions.reduce((total, position) => {
      return total + (position.size * position.entryPrice) / position.leverage;
    }, 0);
  }, [positions]);

  const snapshotEquity = useMemo(() => {
    if (!accountSummary) return 0;
    return accountSummary.collateral + accountSummary.unrealizedPnl;
  }, [accountSummary]);

  const snapshotMarginAvailable = useMemo(() => {
    return snapshotEquity - snapshotUsedMargin;
  }, [snapshotEquity, snapshotUsedMargin]);

  // Transform positions for health calculation
  const positionsForHealth: PositionForHealth[] = useMemo(() => {
    return positions.map(position => ({
      size: position.size,
      entryPrice: position.entryPrice,
      leverage: position.leverage,
      margin: position.margin ?? (position.size * position.entryPrice) / position.leverage,
    }));
  }, [positions]);

  if (isLoading) {
    return (
      <DashboardCard title="Account Health" subtitle="Margin and leverage status">
        <div className="flex items-center justify-center h-32">
          <Spinner />
        </div>
      </DashboardCard>
    );
  }

  // Use calculated snapshot values (accurate on load, static while viewing)
  const accountLeverage = accountSummary?.accountLeverage ?? 0;
  const availableBalance = snapshotMarginAvailable;
  const usedMargin = snapshotUsedMargin;
  const openPositions = positions.length;

  return (
    <DashboardCard title="Account Health" subtitle="Margin and leverage status" className="h-full">
      <div className="space-y-6">
        {/* Health Bar - Only show when there are open positions */}
        {snapshotUsedMargin > 0 ? (
          <MarginHealthBar 
            equity={snapshotEquity}
            usedMargin={snapshotUsedMargin}
            positions={positionsForHealth}
            accountMode={accountMode ?? "cross"}
          />
        ) : (
          <div className="text-sm text-muted-foreground py-2">
            No open positions
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">
              Account Leverage
            </div>
            <div className="text-lg font-semibold">
              {accountLeverage > 0 ? `${accountLeverage.toFixed(2)}x` : "—"}
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">
              Open Positions
            </div>
            <div className="text-lg font-semibold">{openPositions}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">
              Margin Available
            </div>
            <div className="text-lg font-semibold text-bid-foreground">
              ${formatCurrency(availableBalance, 2)}
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">
              Margin Used
            </div>
            <div className="text-lg font-semibold">
              ${formatCurrency(usedMargin, 2)}
            </div>
          </div>
        </div>
      </div>
    </DashboardCard>
  );
};
